import { useEffect, useRef } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { PREFIXO_API, ROTAS } from '@remoto/shared';
import { chamar } from '../api';
import { usarSessao } from '../estado/sessao';
import { fecharSocketDoPainel } from '../socket/painel';
import { PainelDaSessao } from '../componentes/chat/PainelDaSessao';
import { ListaDeSessoes } from './ListaDeSessoes';
import { paginasVisiveis } from './paginas';
import { IconeAviso, IconeMicrofone } from '../componentes/icones';
import { escreverWorkspace, type AbaDoCelular } from './estado';
import { estadoInicial, lerProsaNoCelular } from './memoria';
import { TelaDaProsa } from '../prosa/TelaDaProsa';
import { usarSessaoMestre } from '../sessoes/mestre';
import { lerMotorDaProsa, mestreDaProsa } from '../prosa/escolha';

/**
 * O painel no celular.
 *
 * O workspace de três colunas não cabe num telefone, e espremê-lo faria três
 * painéis ruins no lugar de uma tela boa. Aqui ficam as coisas que se faz de
 * pé, longe do computador: ver as sessões, responder uma permissão, mandar uma
 * mensagem. Editor e árvore não entram — mexer em código no telefone não é
 * conforto, é castigo.
 */
export function NoCelular(): React.JSX.Element {
  const busca = useSearch({ strict: false }) as Record<string, unknown>;
  const navegar = useNavigate();
  const usuario = usarSessao((e) => e.usuario);
  const sair = usarSessao((e) => e.sair);

  /**
   * A aba e a sessão vêm da URL, como tudo no painel (`workspace/estado.ts`).
   *
   * Eram dois `useState`, e isso custava duas coisas medidas: abrir
   * `/?s=ses2` no telefone caía na lista em vez da conversa, e o
   * voltar do aparelho pulava o painel inteiro porque nenhuma aba tinha
   * gravado passo nenhum no histórico.
   */
  const estado = estadoInicial(busca);
  const aba = estado.celular;
  const sessao = estado.sessao;
  const { mestres, carregando: carregandoMestre } = usarSessaoMestre();
  /*
    A Prosa abre com o mestre PREFERIDO, e cai no primeiro quando a
    preferencia ficou orfa. Ler `mestres[0]` aqui faria a escolha guardada
    valer so quando a sessao ja estivesse na URL — ou seja, nunca no
    caminho de quem abre a Prosa pelo botao.
  */
  const mestre = mestreDaProsa(mestres, lerMotorDaProsa());

  const { data: eu } = useQuery({
    queryKey: ['eu'],
    queryFn: () =>
      chamar<{ recursos?: { transcricao?: boolean; notificacoes?: boolean; abaAudio?: boolean } }>(
        ROTAS.auth.eu,
      ),
    staleTime: 5 * 60_000,
  });

  const { data: pendentes = [] } = useQuery({
    queryKey: ['permissoes-pendentes'],
    queryFn: () => chamar<{ id: string }[]>(`${ROTAS.permissoes}?estado=PENDENTE`),
    refetchInterval: 15_000,
  });

  /**
   * Cada passo é um passo no histórico — `push`, nunca `replace`.
   *
   * É o que faz o voltar do Android devolver a tela anterior de dentro do
   * painel em vez de sair dele.
   */
  function ir(mudanca: { celular?: AbaDoCelular; sessao?: string; prosa?: boolean }): void {
    void navegar({ to: '/', search: escreverWorkspace({ ...estado, ...mudanca }) });
  }

  /**
   * No celular a Prosa abre por padrão — o pedido era "um bate-papo com o
   * mestre".
   *
   * Aplicada por NAVEGAÇÃO, uma vez só e com `replace: true`, e não dentro de
   * `estadoInicial`. A diferença é o botão voltar: lida na memória, a
   * preferência ressuscitaria a Prosa por cima da saída que a pessoa acabou de
   * fazer — que é exatamente a cicatriz do `cel` (ver `memoria.ts`). Com
   * `replace` não há passo novo no histórico, e sair continua saindo.
   *
   * Sem mestre no escopo — quem não é ADMIN nunca a vê — a preferência não se
   * aplica e a lista de hoje abre normalmente. Ausência, não tela cinza.
   */
  const jaDecidiu = useRef(false);
  useEffect(() => {
    if (jaDecidiu.current) return;
    if (carregandoMestre) return;
    jaDecidiu.current = true;
    const urlCalada = !busca['prosa'] && !busca['cel'] && !busca['s'];
    if (!urlCalada || !lerProsaNoCelular() || !mestre) return;
    void navegar({
      to: '/',
      search: escreverWorkspace({ ...estado, prosa: true, sessao: mestre.id }),
      replace: true,
    });
  }, [carregandoMestre, mestre, busca, estado, navegar]);

  async function encerrar(): Promise<void> {
    await fetch(PREFIXO_API + ROTAS.auth.logout, { method: 'POST', credentials: 'include' });
    fecharSocketDoPainel();
    sair();
    void navegar({ to: '/entrar' });
  }

  /**
   * Três abas, e a terceira é a porta para o resto do site.
   *
   * A aba "Permissões" saiu: ela não respondia nada — dizia "abra a sessão para
   * ver o que ela quer fazer" e mandava de volta para a primeira aba. O pedido é
   * respondido dentro da conversa, que é onde se enxerga o que a sessão estava
   * fazendo; a contagem do que espera virou aviso na aba Sessões, que é para
   * onde a pessoa iria de qualquer jeito.
   *
   * No lugar dela entrou o menu: pelo telefone não havia caminho nenhum para
   * Avisos, Máquinas ou Uso — e ligar as notificações num aparelho só é possível
   * a partir dele.
   */
  const abas: { id: AbaDoCelular; rotulo: string; aviso?: number }[] = [
    { id: 'sessoes', rotulo: 'Sessões', aviso: pendentes.length },
    { id: 'chat', rotulo: 'Conversa' },
    { id: 'menu', rotulo: 'Menu' },
  ];

  const paginas = paginasVisiveis(usuario?.papel, eu?.recursos).filter((p) => p.id !== 'trabalho');

  /*
    A Prosa toma a tela inteira: sem cabeçalho e sem a barra de abas.
    O cromo mínimo é o pedido, e a saída é o ícone de grade no rodapé dela.
  */
  if (estado.prosa && (sessao || mestre)) {
    return (
      <TelaDaProsa
        sessaoId={sessao || (mestre?.id ?? '')}
        aoSair={() => ir({ prosa: false })}
        aoIrParaSessao={(id) => ir({ sessao: id, prosa: false, celular: 'chat' })}
        aoTrocarDeSessao={(id) => ir({ sessao: id, prosa: true })}
      />
    );
  }

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-zinc-950 font-sans text-zinc-100 antialiased">
      {/*
        `pt-entalhe` e `px-bordas`: o `index.html` pede `viewport-fit=cover`,
        então sem devolver a faixa do aparelho o título encosta no entalhe e,
        deitado, nos cantos arredondados.
      */}
      <header className="flex shrink-0 items-center justify-between border-b border-zinc-800 px-3 py-2 pt-entalhe px-bordas">
        <span className="font-semibold">Remoto</span>
        {/*
          A porta de volta para a Prosa.

          Sem ela a tela era de mao unica: a abertura automatica so dispara com
          a URL calada (`!prosa && !cel && !s`), e sair da Prosa deixa `s=` na
          URL -- entao ela nunca mais abria sozinha e nao havia botao nenhum.
          Medido no aparelho do dono: "entrei uma vez e nunca mais voltou".

          Fica no cabecalho porque o cabecalho existe nas TRES abas; posto na
          lista, sumiria justamente na aba da conversa.
        */}
        {mestre && !estado.prosa ? (
          <button
            type="button"
            onClick={() => ir({ prosa: true, sessao: mestre.id })}
            aria-label="Abrir a Prosa"
            title="Prosa — a conversa de voz com a mestre"
            data-testid="ir-para-prosa"
            className="flex min-h-11 items-center gap-1.5 rounded-full bg-violet-600 px-4 text-sm font-medium text-white"
          >
            <IconeMicrofone tamanho={16} />
            Prosa
          </button>
        ) : null}
      </header>

      <div className="min-h-0 flex-1 overflow-hidden px-bordas">
        {aba === 'sessoes' ? (
          <ListaDeSessoes
            sessaoAtiva={sessao}
            aoEscolher={(escolhida) => ir({ sessao: escolhida.id, celular: 'chat' })}
          />
        ) : aba === 'chat' ? (
          sessao ? (
            // `aoIrParaSessao` também aqui: sem ele, o link de sessão que o
            // mestre escreve viraria texto morto justamente no celular.
            <PainelDaSessao
              id={sessao}
              aoIrParaSessao={(destino) => ir({ sessao: destino, celular: 'chat' })}
            />
          ) : (
            <p className="p-6 text-center text-sm text-zinc-600">
              Escolha uma sessão na aba ao lado.
            </p>
          )
        ) : (
          <div className="h-full overflow-y-auto p-3">
            {pendentes.length > 0 ? (
              <button
                onClick={() => ir({ celular: 'sessoes' })}
                className="mb-3 flex w-full items-center gap-2 rounded-lg border border-amber-900 bg-amber-950/30 px-3 py-2.5 text-left text-sm text-amber-200"
              >
                <IconeAviso tamanho={16} />
                {pendentes.length} pedido(s) esperando resposta
              </button>
            ) : null}
            <ul className="space-y-1">
              {paginas.map((pagina) => (
                <li key={pagina.id}>
                  <Link
                    to={pagina.rota}
                    className="flex items-center gap-3 rounded-lg border border-zinc-800 px-3 py-3 text-sm text-zinc-200 active:bg-zinc-800"
                  >
                    <pagina.Icone />
                    {pagina.rotulo}
                  </Link>
                </li>
              ))}
            </ul>
            {/*
              Quem é a conta aberta ficava numa linha fixa embaixo da barra de
              abas, roubando altura de toda tela para dizer sempre a mesma
              coisa. Aqui ela custa nada e está ao lado do botão que a encerra.
            */}
            <p className="mt-4 px-1 text-xs text-zinc-600">
              {usuario?.nome} · {usuario?.papel.toLowerCase()}
            </p>
            <button
              onClick={() => void encerrar()}
              className="mt-1 w-full rounded-lg border border-zinc-800 px-3 py-2.5 text-sm text-zinc-400 active:bg-zinc-800"
            >
              Sair
            </button>
          </div>
        )}
      </div>

      {/*
        A barra de abas encosta no fundo da tela, onde mora o risco de gestos
        do iPhone. `pb-gesto` empurra os botões para cima dele: sem isso dá
        para ver a aba e não dá para acertá-la.
      */}
      <nav className="flex shrink-0 border-t border-zinc-800 pb-gesto px-bordas">
        {abas.map((item) => (
          <button
            key={item.id}
            onClick={() => ir({ celular: item.id })}
            aria-current={aba === item.id}
            className={
              'min-h-13 flex-1 text-sm transition ' +
              (aba === item.id ? 'bg-zinc-800 text-zinc-100' : 'text-zinc-500')
            }
          >
            {item.rotulo}
            {item.aviso ? <span className="ml-1 text-amber-300">{item.aviso}</span> : null}
          </button>
        ))}
      </nav>
    </div>
  );
}
